import React, { useState, useEffect } from 'react';
import { Table, Typography, Card, Space, Tag, Spin, Alert, Empty, Collapse, Statistic, Row, Col } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined, TrophyOutlined, BarChartOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const MRExamSummaryReport = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalStudents: 0,
    totalExams: 0,
    passRate: 0
  });

  useEffect(() => {
    const fetchAllMRExamResults = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('正在获取MR考试汇总数据，无需认证');
        
        const res = await axios.get('/api/exams/mr-results/all/public');
        
        console.log('获取到MR考试汇总数据:', res.data);
        
        // 计算统计数据
        const students = res.data.length;
        let totalExams = 0;
        let passedExams = 0;
        
        res.data.forEach(student => {
          totalExams += student.examRecords.length;
          passedExams += student.examRecords.filter(exam => exam.passed).length;
        });
        
        setStatistics({
          totalStudents: students,
          totalExams: totalExams,
          passRate: totalExams > 0 ? Math.round((passedExams / totalExams) * 100) : 0
        });
        
        setSummaryData(res.data);
        setLoading(false);
      } catch (err) {
        console.error('获取MR考试汇总数据失败:', err);
        setError(`获取MR考试汇总数据失败: ${err.response?.status} ${err.response?.data?.msg || err.message}`);
        setLoading(false);
      }
    };

    fetchAllMRExamResults();
  }, []);

  const examColumns = [
    {
      title: '考试名称',
      dataIndex: 'examName',
      key: 'examName',
    },
    {
      title: '分数',
      dataIndex: 'score',
      key: 'score',
    },
    {
      title: '结果',
      dataIndex: 'passed',
      key: 'passed',
      render: passed => (
        <Tag color={passed ? 'success' : 'error'} icon={passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {passed ? '通过' : '未通过'}
        </Tag>
      ),
    },
    {
      title: '考试时间',
      dataIndex: 'date',
      key: 'date',
      render: date => new Date(date).toLocaleString(),
      defaultSortOrder: 'descend',
    },
  ];

  const expandedRowRender = (record) => {
    const dataSource = record.examRecords.map((exam, index) => ({
      ...exam,
      key: index
    }));
    
    return (
      <Table 
        columns={examColumns} 
        dataSource={dataSource} 
        pagination={false} 
        rowKey="key" 
      />
    );
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '学校',
      dataIndex: 'school',
      key: 'school',
    },
    {
      title: '班级',
      dataIndex: 'className',
      key: 'className',
    },
    {
      title: '考试次数',
      dataIndex: 'examRecords',
      key: 'examCount',
      render: examRecords => examRecords.length,
    },
    {
      title: '通过次数',
      dataIndex: 'examRecords',
      key: 'passCount',
      render: examRecords => examRecords.filter(exam => exam.passed).length,
    },
    {
      title: '平均分',
      dataIndex: 'examRecords',
      key: 'avgScore',
      render: examRecords => {
        const total = examRecords.reduce((sum, exam) => sum + exam.score, 0);
        return Math.round(total / examRecords.length);
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>加载MR考试汇总数据...</p>
      </div>
    );
  }

  if (error) {
    return <Alert message="错误" description={error} type="error" showIcon />;
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>MR操作考试汇总报表</Title>
        
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic 
                title="参与学生数" 
                value={statistics.totalStudents} 
                prefix={<UserOutlined />} 
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic 
                title="考试总次数" 
                value={statistics.totalExams} 
                prefix={<BarChartOutlined />} 
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic 
                title="通过率" 
                value={statistics.passRate} 
                suffix="%" 
                prefix={<TrophyOutlined />}
                valueStyle={{ color: statistics.passRate > 60 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
        
        {summaryData.length > 0 ? (
          <Table 
            columns={columns} 
            dataSource={summaryData.map((item, index) => ({ ...item, key: index }))} 
            expandable={{ expandedRowRender }}
            pagination={{ pageSize: 10 }}
            rowKey="key"
          />
        ) : (
          <Empty description="暂无MR操作考试记录" />
        )}
      </Space>
    </Card>
  );
};

export default MRExamSummaryReport; 